/**
 * /select-workspace — durable home of the >=2-membership picker.
 *
 * Three meaningful states (per the Next.js sibling):
 *   - not authenticated → /login
 *   - already tenant-active → /dashboard (somehow landed here)
 *   - identity-only session → render the picker
 *
 * The login page opens the picker INLINE for the happy path (the
 * MyWorkspacesResponse is already on the login response). This route
 * is the fallback for page refreshes during the pick AND for the
 * dashboard guard bouncing an identity-only session away from tenant
 * UI — it re-derives the choices from GET /auth/my-workspaces.
 *
 * Migration note: 1:1 port of
 * `frontend/src/app/(auth)/select-workspace/page.tsx`. The
 * MY_WORKSPACES_KEY is inlined here (the Next.js project imports it
 * from company-switcher.tsx, which is a Phase 3 file).
 */

import { useEffect, useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth, safeNext } from "@/lib/auth";
import { postAuthRedirect } from "@/lib/auth/safe-next";
import { authApi, toApiError, isTotpEnrollmentRequired } from "@/lib/api";
import { WorkspacePickerDialog } from "@/components/auth/workspace-picker-dialog";

// Single source of truth for the my-workspaces query key so the
// picker route, future in-app switcher, and any other consumer all
// invalidate the SAME cache entry.
export const MY_WORKSPACES_KEY = ["my-workspaces"] as const;

const selectWorkspaceSearchSchema = z.object({
  next: z.string().optional(),
});

export const Route = createFileRoute("/_auth/select-workspace")({
  validateSearch: selectWorkspaceSearchSchema,
  component: SelectWorkspacePage,
});

function SelectWorkspacePage() {
  const navigate = useNavigate();
  const { next: nextRaw } = Route.useSearch();
  const { user, loading, identityOnly, activate, logout } = useAuth();

  const nextPath = safeNext(nextRaw);

  // Only fetch the membership list once we know it's an identity-only
  // session (the only state this page is meaningful in). The list is
  // per-active-session and must never be served stale across an
  // activate(), so don't cache it.
  const q = useQuery({
    queryKey: MY_WORKSPACES_KEY,
    queryFn: authApi.getMyWorkspaces,
    enabled: identityOnly,
    staleTime: 0,
    gcTime: 0,
  });

  const workspaces = useMemo(() => q.data?.others ?? [], [q.data]);

  // Route the non-picker states out. The `nextPath` we capture here is
  // a static prop of THIS instance — when `user` flips to null after a
  // logout the effect re-fires once and navigates, then this route
  // unmounts. The dashboard-layout pathname-prefix guard does not
  // apply here (we ARE on /select-workspace) so there's no compounding
  // ?next= risk: nextPath is already a safe path.
  useEffect(() => {
    if (loading) return;
    if (!user) {
      void navigate({
        to: "/login",
        search: { next: nextPath },
        replace: true,
      });
      return;
    }
    if (!identityOnly) {
      const target = postAuthRedirect(nextPath, user.tenant?.slug);
      void navigate({ to: target, replace: true });
    }
  }, [loading, user, identityOnly, navigate, nextPath]);

  async function onPick(slug: string) {
    try {
      const res = await activate(slug);
      if (res.totp_required) {
        void navigate({
          to: "/2fa-verify",
          search: { next: nextPath },
          replace: true,
        });
        return;
      }
      // activate() rotates cookies + calls refresh() internally; the
      // picked `slug` IS the active workspace now, so we can use it
      // directly without a second /me round-trip.
      const target = postAuthRedirect(nextPath, slug);
      void navigate({ to: target, replace: true });
    } catch (err) {
      if (isTotpEnrollmentRequired(err)) {
        void navigate({
          to: "/2fa-setup",
          search: { required: 1, next: nextPath },
          replace: true,
        });
        return;
      }
      toast.error(toApiError(err).message);
      throw err;
    }
  }

  if (loading || !user || !identityOnly) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl">Choose a workspace</CardTitle>
        <CardDescription>
          You&apos;re signed in. Pick the company you want to open —
          you can switch any time from the account menu.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {q.isLoading && (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        )}
        {q.isError && (
          <p role="alert" className="text-sm text-destructive">
            Couldn&apos;t load your workspaces. Try again.
          </p>
        )}
        {!q.isLoading && !q.isError && workspaces.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Your account isn&apos;t a member of any active company.
            Contact your administrator.
          </p>
        )}
        <Button
          variant="outline"
          className="w-full"
          onClick={async () => {
            await logout();
            void navigate({ to: "/login", replace: true });
          }}
        >
          Sign in as a different user
        </Button>
      </CardContent>

      <WorkspacePickerDialog
        open={!q.isLoading && !q.isError && workspaces.length > 0}
        workspaces={workspaces}
        onCancel={async () => {
          // Closing the picker on this route has no form to fall back
          // to — the only honest exit is to sign out.
          await logout();
          void navigate({ to: "/login", replace: true });
        }}
        onSelect={onPick}
      />
    </Card>
  );
}
